import { MSK_TIMEZONE } from '@/utils/mskDate.js'

export const GENERATION_IN_PROGRESS_STATUSES = ['new', 'generating']
export const GENERATION_TERMINAL_STATUSES = ['generated', 'generation_failed']
export const GENERATION_LIMIT_CODE = 'generation_limit_exceeded'
export const GENERATION_IN_PROGRESS_CODE = 'generation_in_progress'
export const CONSENT_REQUIRED_CODE = 'consent_required'
export const PHOTO_AI_CONSENT_TYPE = 'photo_ai_processing'
const LEGACY_LIMIT_DETAIL = 'user generation limit exceeded'

function detailToString(detail) {
  if (Array.isArray(detail)) {
    return detail.filter(Boolean).join(' ')
  }

  return typeof detail === 'string' ? detail : ''
}

export function isGenerationInProgressStatus(status) {
  return GENERATION_IN_PROGRESS_STATUSES.includes(status)
}

export function isGenerationTerminalStatus(status) {
  return GENERATION_TERMINAL_STATUSES.includes(status)
}

export function isGenerationLimitError(error) {
  const status = error?.response?.status
  const data = error?.response?.data
  const detail = detailToString(data?.detail)

  if (status === 429) {
    return true
  }

  if (data?.code === GENERATION_LIMIT_CODE) {
    return true
  }

  return detail.toLowerCase().includes(LEGACY_LIMIT_DETAIL)
}

export function isGenerationInProgressConflict(error) {
  const status = error?.response?.status
  const data = error?.response?.data

  return status === 409 || data?.code === GENERATION_IN_PROGRESS_CODE
}

/** Cancel/retry по уже заменённой записи (новый id после PUT/retry) или не в процессе. */
export function isIgnorableCancelGenerationError(error) {
  if (isGenerationInProgressConflict(error)) {
    return true
  }

  const status = error?.response?.status
  const code = error?.response?.data?.code

  return status === 404 || code === 'not_generating'
}

export function isConsentRequiredError(error) {
  const status = error?.response?.status
  const data = error?.response?.data

  return status === 403 && data?.code === CONSENT_REQUIRED_CODE
}

export function getGenerationHttpErrorMessage(error) {
  const data = error?.response?.data
  const detail = detailToString(data?.detail)

  return detail || 'Не удалось сгенерировать изображение'
}

export function getGenerationRecordErrorMessage(generation) {
  if (!generation) {
    return undefined
  }

  if (typeof generation.generation_error === 'string' && generation.generation_error) {
    return generation.generation_error
  }

  return undefined
}

export function formatGenerationLimitMessage({
  resetsAt,
  limit = 3,
} = {}) {
  if (resetsAt) {
    try {
      const time = new Intl.DateTimeFormat('ru-RU', {
        timeZone: MSK_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(resetsAt))

      return `Ты использовал все ${limit} генерации на сегодня. Новые — после ${time} мск`
    } catch (e) {
      // fall through
    }
  }

  return `Ты использовал все ${limit} генерации на сегодня. Новые появятся после сброса лимита`
}

export function getGenerationLimitResetsAt(error, profile) {
  return error?.response?.data?.resets_at
    || profile?.generation_limit_resets_at
    || null
}

export function getGenerationLimitValue(error, profile, fallback = 3) {
  const fromError = Number(error?.response?.data?.limit)
  if (Number.isFinite(fromError) && fromError > 0) {
    return fromError
  }

  const fromProfile = Number(profile?.generation_limit)
  if (Number.isFinite(fromProfile) && fromProfile > 0) {
    return fromProfile
  }

  return fallback
}

export function isGenerationRetryable(generation) {
  if (!generation || generation.status !== 'generation_failed') {
    return false
  }

  if (generation.is_retryable === false) {
    return false
  }

  if (generation.error_action === 'set_target' || generation.error_action === 'replace_photo') {
    return false
  }

  return true
}
